import React, { useMemo, useState, useContext, useEffect } from 'react';
import { Grid, TextField, FormControl, InputLabel, Select } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import helpers from 'app/utils/helpers';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { FormServiceContext } from 'app/utils/contexts';
import certificationEndpoints from 'app/api/services/certificationEndpoints';
import { Field } from 'formik';
import { clearServiceDocuments } from 'app/store/slices/services';
import { SERVICES_GROUPS } from 'app/config/services';
import { fetchLineSecretary } from '../../../../../store/slices/line';
import notification from '../../../../../config/notification';

function RegistrationStatus(props) {
  const dispatch = useDispatch();

  const authUser = useSelector(state => state.auth.user);
  const service = useSelector(state => state.services.details);
  const request = useSelector(state => state.requests.details);
  const activeCompany = useSelector(state => state.company.activeCompany);
  const internalNewRequest = useSelector(state => state.forms.internalNewRequest);
  const allUsers = useSelector(state => state.auth.activeInactiveUsers);
  const lineSecretary = useSelector(state => state.line.lineSecretary);

  const { values, handleChange, setFieldValue, onChangeDate, classes, canEdit } = props;

  const { isInternalForm, user, registrationType, setRegistrationType } = useContext(
    FormServiceContext
  );

  const processNumber = useMemo(() => {
    return request.nuProcesso || ' ';
  }, [request.nuProcesso]);

  const [showFieldPersonType, setShowFieldPersonType] = useState(false);
  const [showCRCDate, setShowCRCDate] = useState(false);
  const [showSituation, setShowSituation] = useState(false);
  const [showProcessNumber] = useState(true);
  const [canChangeUser, setCanChangeUser] = useState(false);

  const isLine = useMemo(() => {
    const cdServico = service?.cdServico || internalNewRequest?.cdServico;
    return !!(SERVICES_GROUPS.LINES_SLIC.includes(cdServico) || SERVICES_GROUPS.LINES_STRIP.includes(cdServico));
  },[internalNewRequest, service]);

  useEffect(() => {
    const cdServico = service?.cdServico || internalNewRequest?.cdServico;
    if (cdServico) {
      if (SERVICES_GROUPS.COMPANY.includes(cdServico)) {
        setShowFieldPersonType(true);
        setShowCRCDate(true);
        setShowSituation(true);
      }
    }

  }, [internalNewRequest, processNumber, service]);

  useEffect(() => {
    (async () => {
      if(isInternalForm && isLine){
        if(!lineSecretary){
          await dispatch(fetchLineSecretary());
        }
        if(authUser?.username !== String(lineSecretary)){
          setCanChangeUser(false);
          return;
        }
        setCanChangeUser(true);
      }
    })();
  },[dispatch, isInternalForm, isLine, lineSecretary, authUser]);

  const [username] = useState(user?.nmUsuario ? user.nmUsuario : '');
  const [certificate, setCertificate] = useState({});

  const organSituation = useMemo(() => {
    let result = 'Inativa';
    let boSituacaoOrgao = false;

    const isInternalNewRequest = Object.keys(internalNewRequest).length > 0;

    if (!isInternalForm && !isInternalNewRequest) {
      boSituacaoOrgao = activeCompany.boSituacaoOrgao ? activeCompany.boSituacaoOrgao : values.boSituacaoOrgao
        ? values.boSituacaoOrgao
        : null;

    } else {
      boSituacaoOrgao = values.boSituacaoOrgao ? values.boSituacaoOrgao : null;
    }
    if (boSituacaoOrgao) result = 'Ativa';

    if (certificate.dtVencimentoCrc) {
      const diffDays = moment.utc().diff(certificate.dtVencimentoCrc, 'days');
      if (diffDays >= 0) result = 'Vencida';
    }

    return result;
  }, [activeCompany.boSituacaoOrgao, certificate.dtVencimentoCrc, internalNewRequest, isInternalForm, values.boSituacaoOrgao]);

  useEffect(() => {
    ( async () => {
      const isInternalNewRequest = Object.keys(internalNewRequest).length > 0;
      if (values.certificado) {
        setCertificate(values.certificado);
      } else {
        if (!isInternalNewRequest && !isInternalForm && activeCompany && activeCompany.certificado) {
          setCertificate(activeCompany.certificado);
        } else if (isInternalForm) {
          if(request.cdUsuarioResponsavel) {
            props.setFieldValue("cdUsuarioResponsavelTmp",request.cdUsuarioResponsavel);
          }
          if (request.cdSolicitacao) {
            try {
              const response = await certificationEndpoints.getCertificates(request.cdSolicitacao);
              if (response?.status === 200 && response?.data && response?.data.length > 0) {
                const cert = response.data.shift();
                if (cert.boAssinado) {
                  setCertificate(cert);
                }
              }
            } catch (error) { }
          }

        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, isInternalForm, request]);

  const handleRegistrationType = (e, values) => {
    const type = e.target.value;
    setRegistrationType(type);
    values.tpCadastro = type;
    dispatch(clearServiceDocuments());
  };

  return (
    <>
      {isInternalForm && (
        <Grid item xs={12} sm={isInternalForm ? 6 : 4}>
          {!canChangeUser ? (
            <TextField
              name="usuarioResponsavel"
              label="Usuário Responsável"
              margin="dense"
              fullWidth
              disabled
              value={username}
              InputLabelProps={{
                shrink: true,
              }}
            />
          ) : (
            <>
              <FormControl variant="filled" margin="dense" fullWidth className={classes.formControl}>
                <InputLabel id="cdUsuarioResponsavel" shrink={true}>
                  Usuário Responsável
                </InputLabel>
                <Field
                  name="cdUsuarioResponsavelTmp"
                  id="cdUsuarioResponsavelTmp"
                  native
                  as={Select}
                  disabled={!canEdit}
                  onChange={e => {
                    const userSelected = allUsers.find(user => user.cdUsuario === e.target.value);
                    if (userSelected && !userSelected.boAtivo) {
                      notification('Este usuário encontra-se inativo neste momento. Por favor, selecione outro.', {
                        type: 'error',
                      });
                      setFieldValue('cdUsuarioResponsavelTmp',lineSecretary);
                      return;
                    }
                    if (handleChange){
                      handleChange(e);
                    }
                  }}
                >
                  {allUsers.map(u => {
                    return <option key={`user-${u.cdUsuario}`} value={u.cdUsuario}>{u.nmUsuario}</option>
                  })}
                </Field>
              </FormControl>
            </>
          )}
        </Grid>
      )}
      {showProcessNumber && (
        <Grid item xs={12} sm={isInternalForm ? 6 : 4}>
          <TextField
            name="nuProcesso"
            label="Número do processo"
            margin="dense"
            fullWidth
            disabled
            value={processNumber}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      )}
      {showCRCDate && (
        <Grid item xs={12} sm={isInternalForm ? 6 : 4}>
          <KeyboardDatePicker
            name="dtVencimentoCrc"
            label="Data Vencimento Certidão"
            format="DD/MM/YYYY"
            autoOk
            clearable
            clearLabel={'Limpar'}
            value={helpers.formatarData(certificate?.dtVencimentoCrc)}
            invalidLabel="Data inválida"
            invalidDateMessage="Data inválida"
            maxDateMessage="Data inválida"
            minDateMessage="Data inválida"
            emptyLabel={''}
            margin="dense"
            onChange={v => onChangeDate(values, 'dtVencimentoCrc', v)}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            fullWidth
            disabled
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      )}
      {showSituation && (
        <Grid item xs={12} sm={isInternalForm ? 6 : 4}>
          <TextField
            name="boSituacaoOrgao"
            label="Situação no Órgão"
            margin="dense"
            fullWidth
            disabled
            value={organSituation}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      )}
      {showFieldPersonType && (
        <Grid item xs={12} sm={4}>
          <FormControl variant="filled" margin="dense" fullWidth className={classes.formControl}>
            <InputLabel id="tpCadastro" shrink={true}>
              Tipo de cadastro
            </InputLabel>
            <Field
              name="tpCadastro"
              id="tpCadastro"
              native
              disabled
              as={Select}
              value={registrationType}
              onChange={e => handleRegistrationType(e, values)}
            >
              <option value={'company'}>Pessoa Jurídica</option>
              <option value={'person'}>Pessoa Física</option>
            </Field>
          </FormControl>
        </Grid>
      )}
    </>
  );
}

export default RegistrationStatus;
